// Vitals Display Mapping Utility
// Provides display labels and metadata for vitals parameters in the frontend

// Define vitals parameters with display information
const vitalsParameters = [
  { 
    key: 'vitals_temp_c', 
    label: 'Temperature', 
    units: '°C', 
    shortLabel: 'Temp',
    category: 'Vitals',
    type: 'numeric',
    normalRange: { min: 36.1, max: 37.2 }
  },
  { 
    key: 'vitals_sbp', 
    label: 'Systolic Blood Pressure', 
    units: 'mmHg', 
    shortLabel: 'SBP',
    category: 'Vitals',
    type: 'numeric',
    normalRange: { min: 90, max: 140 }
  },
  { 
    key: 'vitals_dbp', 
    label: 'Diastolic Blood Pressure', 
    units: 'mmHg', 
    shortLabel: 'DBP',
    category: 'Vitals',
    type: 'numeric',
    normalRange: { min: 60, max: 90 }
  },
  { 
    key: 'vitals_rhr', 
    label: 'Resting Heart Rate', 
    units: 'bpm', 
    shortLabel: 'HR',
    category: 'Vitals',
    type: 'numeric',
    normalRange: { min: 60, max: 100 }
  },
  { 
    key: 'vitals_rr', 
    label: 'Respiratory Rate', 
    units: 'breaths/min', 
    shortLabel: 'RR',
    category: 'Vitals',
    type: 'numeric',
    normalRange: { min: 12, max: 20 }
  },
  { 
    key: 'vitals_map', 
    label: 'Mean Arterial Pressure', 
    units: 'mmHg', 
    shortLabel: 'MAP',
    category: 'Vitals',
    type: 'numeric',
    normalRange: { min: 70, max: 100 }
  },
  { 
    key: 'vitals_pp', 
    label: 'Pulse Pressure', 
    units: 'mmHg', 
    shortLabel: 'PP',
    category: 'Vitals',
    type: 'numeric',
    normalRange: { min: 30, max: 50 }
  },
  { 
    key: 'vitals_spo2', 
    label: 'Oxygen Saturation', 
    units: '%', 
    shortLabel: 'SpO2',
    category: 'Vitals',
    type: 'numeric',
    normalRange: { min: 95, max: 100 }
  },
  { 
    key: 'vitals_sirs', 
    label: 'SIRS Score', 
    units: 'points', 
    shortLabel: 'SIRS',
    category: 'Vitals',
    type: 'numeric',
    normalRange: { min: 0, max: 0 }
  },
  { 
    key: 'vitals_hrv', 
    label: 'Heart Rate Variability', 
    units: 'ms', 
    shortLabel: 'HRV',
    category: 'Vitals',
    type: 'numeric',
    normalRange: { min: 20, max: 50 }
  },
  { 
    key: 'vitals_vo2max', 
    label: 'VO2 Max', 
    units: 'ml/kg/min', 
    shortLabel: 'VO2 Max',
    category: 'Vitals',
    type: 'numeric',
    normalRange: { min: 25, max: 60 }
  },
  { 
    key: 'vitals_pain', 
    label: 'Pain Level', 
    units: '0-10 scale', 
    shortLabel: 'Pain',
    category: 'Vitals',
    type: 'numeric',
    normalRange: { min: 0, max: 3 }
  },
  { 
    key: 'vitals_weight', 
    label: 'Weight', 
    units: 'kg', 
    shortLabel: 'Weight',
    category: 'Vitals',
    type: 'numeric',
    normalRange: null // Varies by individual
  },
  { 
    key: 'vitals_height', 
    label: 'Height', 
    units: 'cm', 
    shortLabel: 'Height',
    category: 'Vitals',
    type: 'numeric',
    normalRange: null // Varies by individual
  },
  { 
    key: 'vitals_bmi', 
    label: 'Body Mass Index', 
    units: 'kg/m²', 
    shortLabel: 'BMI',
    category: 'Vitals',
    type: 'numeric',
    normalRange: { min: 18.5, max: 24.9 }
  }
];

/**
 * Creates mapping objects for vitals parameters
 */
const createVitalsDisplayMapping = () => {
  const keyToLabelMap = {};
  const keyToShortLabelMap = {};
  const keyToUnitsMap = {};
  const keyToMetadataMap = {};
  
  vitalsParameters.forEach(param => {
    keyToLabelMap[param.key] = param.label;
    keyToShortLabelMap[param.key] = param.shortLabel;
    keyToUnitsMap[param.key] = param.units;
    keyToMetadataMap[param.key] = param;
  });
  
  return { keyToLabelMap, keyToShortLabelMap, keyToUnitsMap, keyToMetadataMap };
};

const { keyToLabelMap, keyToShortLabelMap, keyToUnitsMap, keyToMetadataMap } = createVitalsDisplayMapping();

/**
 * Gets the display label for a vitals parameter
 * @param {string} parameterKey - Standardized vitals parameter key
 * @returns {string} - Human-readable display label
 */
export const getVitalsDisplayLabel = (parameterKey) => {
  return keyToLabelMap[parameterKey] || parameterKey;
};

/**
 * Gets the short display label for a vitals parameter
 * @param {string} parameterKey - Standardized vitals parameter key
 * @returns {string} - Short display label
 */
export const getVitalsShortLabel = (parameterKey) => {
  return keyToShortLabelMap[parameterKey] || parameterKey;
};

/**
 * Gets the units for a vitals parameter
 * @param {string} parameterKey - Standardized vitals parameter key
 * @returns {string} - Units string
 */
export const getVitalsUnits = (parameterKey) => {
  return keyToUnitsMap[parameterKey] || '';
};

/**
 * Gets the full metadata for a vitals parameter
 * @param {string} parameterKey - Standardized vitals parameter key
 * @returns {Object|null} - Parameter metadata or null if not found
 */
export const getVitalsMetadata = (parameterKey) => {
  return keyToMetadataMap[parameterKey] || null;
};

/**
 * Gets formatted display text with units for a vitals parameter
 * @param {string} parameterKey - Standardized vitals parameter key
 * @param {boolean} useShortLabel - Whether to use short label
 * @returns {string} - Formatted display text with units
 */
export const getVitalsDisplayWithUnits = (parameterKey, useShortLabel = false) => {
  const label = useShortLabel ? getVitalsShortLabel(parameterKey) : getVitalsDisplayLabel(parameterKey);
  const units = getVitalsUnits(parameterKey);
  return units ? `${label} (${units})` : label;
};

/**
 * Checks if a value is within normal range for a vitals parameter
 * @param {string} parameterKey - Standardized vitals parameter key
 * @param {number} value - Value to check
 * @returns {boolean|null} - True if normal, false if abnormal, null if no range defined
 */
export const isVitalsValueNormal = (parameterKey, value) => {
  const metadata = getVitalsMetadata(parameterKey);
  if (!metadata || !metadata.normalRange || typeof value !== 'number') {
    return null;
  }
  
  const { min, max } = metadata.normalRange;
  return value >= min && value <= max;
};

/**
 * Gets all vitals parameters
 * @returns {Array<Object>} - Array of all vitals parameter metadata
 */
export const getAllVitalsParameters = () => {
  return [...vitalsParameters];
};

/**
 * Gets vitals parameters by type
 * @param {string} type - Parameter type filter
 * @returns {Array<Object>} - Filtered array of vitals parameter metadata
 */
export const getVitalsParametersByType = (type) => {
  return vitalsParameters.filter(param => param.type === type);
};

/**
 * Checks if a parameter key is a standardized vitals parameter
 * @param {string} parameterKey - Parameter key to check
 * @returns {boolean} - True if it's a standardized vitals parameter
 */
export const isVitalsParameter = (parameterKey) => {
  return typeof parameterKey === 'string' && parameterKey.startsWith('vitals_') && keyToLabelMap.hasOwnProperty(parameterKey);
};

/**
 * Transforms vitals data for display in the flowsheet
 * @param {Object} vitalsData - Raw vitals data with standardized keys
 * @returns {Array<Object>} - Array of display objects for flowsheet
 */
export const transformVitalsForDisplay = (vitalsData) => {
  if (!vitalsData || typeof vitalsData !== 'object') {
    return [];
  }
  
  return Object.entries(vitalsData)
    .filter(([key]) => isVitalsParameter(key))
    .map(([key, value]) => ({
      key,
      label: getVitalsDisplayLabel(key),
      shortLabel: getVitalsShortLabel(key),
      value,
      units: getVitalsUnits(key),
      displayValue: getVitalsUnits(key) ? `${typeof value === 'object' && value !== null && value.value !== undefined ? value.value : value} ${getVitalsUnits(key)}` : (typeof value === 'object' && value !== null && value.value !== undefined ? value.value : value),
      isNormal: isVitalsValueNormal(key, parseFloat(value)),
      category: 'Vitals'
    }));
};

// Export the parameters array for external use
export { vitalsParameters };
